import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import { REVENUE_STACKED_AREA_CHART_HEIGHT } from "../revenue/constants";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { REVIEWS_COLORS, REVIEWS_CATEGORY_LABELS } from "./constants";
import type { CategoryKey, RatingTrendPoint } from "./types";

function ReviewsTrendTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number }>;
  label?: string;
  series: Array<{ key: string; label: string; color: string }>;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="border-border/50 bg-background grid min-w-[10rem] gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">
              {Number(item.value ?? 0).toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const CATEGORY_COLORS: Record<CategoryKey, string> = {
  purity: REVIEWS_COLORS.purity,
  comfort: REVIEWS_COLORS.comfort,
  service: REVIEWS_COLORS.service,
};

interface ReviewsTrendChartProps {
  data: RatingTrendPoint[];
  categoriesSupported: boolean;
  categoryKeys: CategoryKey[];
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const ReviewsTrendChart: React.FC<ReviewsTrendChartProps> = ({
  data,
  categoriesSupported,
  categoryKeys,
  onExport,
  exportDisabled,
}) => {
  const { t } = useTranslation();

  const series = useMemo(() => {
    if (categoriesSupported && categoryKeys.length > 0) {
      return categoryKeys.map((key) => ({
        key,
        label: t(
          `analytics.reviews.categories.${key}`,
          REVIEWS_CATEGORY_LABELS[key] ?? key,
        ),
        color: CATEGORY_COLORS[key] ?? REVIEWS_COLORS.purity,
      }));
    }

    return [
      {
        key: "overallRating",
        label: t("analytics.reviews.kpi.overallRating", "Overall Rating"),
        color: REVIEWS_COLORS.purity,
      },
    ];
  }, [categoriesSupported, categoryKeys, t]);

  const chartConfig = series.reduce<ChartConfig>((config, item) => {
    config[item.key] = {
      label: item.label,
      color: item.color,
    };
    return config;
  }, {});
  const chartInstanceId = useAnalyticsChartInstanceId();

  return (
    <RevenueChartCard
      title={t("analytics.reviews.trendTitle", "Overall rating over time")}
      icon={<ChartLine />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex flex-col gap-3">
        <ChartContainer
          config={chartConfig}
          className="!aspect-auto w-full"
          style={{ height: REVENUE_STACKED_AREA_CHART_HEIGHT }}
        >
          <AreaChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <AnalyticsAreaFillDefs
                seriesKeys={series.map((item) => item.key)}
                chartInstanceId={chartInstanceId}
              />
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
              className="text-muted-foreground text-xs"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={40}
              domain={[0, 5]}
              ticks={[0, 1, 2, 3, 4, 5]}
              className="text-muted-foreground text-xs"
            />
            <ChartTooltip
              cursor={false}
              content={<ReviewsTrendTooltip series={series} />}
            />
            {series.map((item) => (
              <Area
                key={item.key}
                type="monotone"
                dataKey={item.key}
                connectNulls
                stroke={`var(--color-${item.key})`}
                fill={analyticsAreaFillUrl(item.key, chartInstanceId)}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ChartContainer>
        <RevenueChartLegend items={series} />
      </div>
    </RevenueChartCard>
  );
};
